import { useEffect } from 'react';

/**
 * Subscribe to a window-level DOM event for the lifetime of the component.
 * Automatically attaches on mount + deps change, detaches on unmount.
 * Typed by event name so the handler argument is properly inferred
 * (e.g. KeyboardEvent for 'keydown', StorageEvent for 'storage').
 *
 * Replaces the manual pattern:
 *   useEffect(() => {
 *     window.addEventListener('keydown', handler);
 *     return () => window.removeEventListener('keydown', handler);
 *   }, [...]);
 */
export function useWindowEvent<K extends keyof WindowEventMap>(
  event: K,
  handler: (e: WindowEventMap[K]) => void,
  deps: React.DependencyList = [],
): void {
  useEffect(() => {
    window.addEventListener(event, handler);
    return () => window.removeEventListener(event, handler);
    // Callers pass an explicit deps array for the handler's closure;
    // event/handler are intentionally excluded from auto-tracking.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
